import { Box3, BufferGeometry, Line, LineBasicMaterial, Quaternion, Vector3 } from "three";
import { Text } from "troika-three-text";
import { Service } from "@modules/module";
import { mainScene } from "@modules/rendering/mainScene";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { camera } from "@modules/rendering/camera";
import { busService } from "@modules/bus";
import { configurePostprocessingOverlay } from "@modules/rendering/postprocessing/PostprocessingOverlay";
/**
 * Service responsible to display debug text next to targets. The service can shows multiple targets for different
 * modules, services etc.
 */
class DebugTextService extends Service {
    constructor() {
        super(busService.connection("DebugTextService"));
        this.textScopes = new Map();
        this.updateCounter = 0;
    }
    showDebugText(identifier, getDebugTextTargets) {
        let debugTextInfo = this.textScopes.get(identifier);
        if (debugTextInfo !== undefined)
            return;
        debugTextInfo = {
            modelTextMap: new Map(),
            modelLineMap: new Map(),
            modelTrackableObjectMap: new Map(),
            labelOffsetMap: new Map(),
            labelAnchorMap: new Map(),
            lineTargetMap: new Map(),
            getTrackableObjects: getDebugTextTargets
        };
        this.textScopes.set(identifier, debugTextInfo);
        const trackableObjects = getDebugTextTargets();
        for (const trackableObject of getDebugTextTargets()) {
            const label = this.createDebugText(trackableObject);
            const line = this.createDebugLine(trackableObject);
            mainScene.add(label);
            mainScene.add(line);
            debugTextInfo.modelTextMap.set(trackableObject.uid, label);
            debugTextInfo.modelLineMap.set(trackableObject.uid, line);
        }
        this.createTrackableObjectMap(debugTextInfo, trackableObjects);
        // We use one interval to handle all scopes
        if (this.intervalHandle !== undefined)
            return;
        this.intervalHandle = setInterval(() => { this.updateDebugText(); }, 500);
    }
    hideDebugText(scopeID) {
        const debugTextInfo = this.textScopes.get(scopeID);
        if (debugTextInfo === undefined)
            return;
        for (const label of debugTextInfo.modelTextMap.values()) {
            mainScene.remove(label);
            label.geometry.dispose();
        }
        for (const line of debugTextInfo.modelLineMap.values()) {
            mainScene.remove(line);
            line.geometry.dispose();
            if (line.material instanceof LineBasicMaterial)
                line.material.dispose();
        }
        debugTextInfo.modelTextMap.clear();
        debugTextInfo.modelLineMap.clear();
        this.textScopes.delete(scopeID);
        // Only clear interval if no text is shown
        if (this.intervalHandle !== undefined && this.textScopes.size === 0) {
            clearInterval(this.intervalHandle);
            this.intervalHandle = undefined;
        }
    }
    /// Private update methods ///
    updateDebugText() {
        for (const debugTextInfo of this.textScopes.values()) {
            const trackableObjects = debugTextInfo.getTrackableObjects();
            const labelUIDs = Array.from(debugTextInfo.modelTextMap.keys());
            const activeShapeUIDs = trackableObjects.map(cs => cs.uid);
            const labelsToAdd = Array
                .from(activeShapeUIDs)
                .filter(labelUID => !labelUIDs.includes(labelUID));
            const labelsToRemove = labelUIDs
                .filter(labelUID => !activeShapeUIDs.includes(labelUID));
            const labelsToUpdate = Array
                .from(activeShapeUIDs)
                .filter(labelUID => labelUIDs.includes(labelUID));
            this.createTrackableObjectMap(debugTextInfo, trackableObjects);
            this.updateLabelOffsets(debugTextInfo, trackableObjects);
            this.removeDebugTexts(debugTextInfo, labelsToRemove);
            this.updateDebugTexts(debugTextInfo, labelsToUpdate);
            this.createDebugTexts(debugTextInfo, labelsToAdd);
            this.updateTextToLookAtCamera(debugTextInfo);
            this.updateCounter = (this.updateCounter + 1) % 4;
        }
    }
    createDebugTexts(debugTextInfo, debugTextToAdd) {
        for (const labelUID of debugTextToAdd) {
            const trackableObject = debugTextInfo.modelTrackableObjectMap.get(labelUID);
            if (trackableObject === undefined)
                continue;
            const text = this.createDebugText(trackableObject);
            const line = this.createDebugLine(trackableObject);
            mainScene.add(text);
            mainScene.add(line);
            debugTextInfo.modelTextMap.set(trackableObject.uid, text);
            debugTextInfo.modelLineMap.set(trackableObject.uid, line);
        }
    }
    updateDebugTexts(debugTextInfo, debugTextToUpdate) {
        var _a, _b, _c, _d, _e;
        for (const labelUID of debugTextToUpdate) {
            const trackableObject = debugTextInfo.modelTrackableObjectMap.get(labelUID);
            if (trackableObject === undefined)
                continue;
            const text = debugTextInfo.modelTextMap.get(labelUID);
            const line = debugTextInfo.modelLineMap.get(labelUID);
            if (text === undefined || line === undefined)
                continue;
            text.text = trackableObject.name;
            text.fontSize = (_a = trackableObject.fontSize) !== null && _a !== void 0 ? _a : 0.06;
            text.color = (_b = trackableObject.color) !== null && _b !== void 0 ? _b : "black";
            text.outlineColor = (_c = trackableObject.outlineColor) !== null && _c !== void 0 ? _c : "black";
            text.outlineWidth = (_d = trackableObject.outlineWidth) !== null && _d !== void 0 ? _d : 0;
            text.sync(() => {
                this.configureTextMaterial(text);
            });
            if (line.material instanceof LineBasicMaterial)
                line.material.color.set((_e = trackableObject.color) !== null && _e !== void 0 ? _e : "black");
        }
    }
    removeDebugTexts(debugTextInfo, debugTextToRemove) {
        for (const textUID of debugTextToRemove) {
            const text = debugTextInfo.modelTextMap.get(textUID);
            const line = debugTextInfo.modelLineMap.get(textUID);
            debugTextInfo.modelTextMap.delete(textUID);
            debugTextInfo.modelLineMap.delete(textUID);
            if (text === undefined)
                continue;
            mainScene.remove(text);
            text.geometry.dispose();
            if (line !== undefined) {
                mainScene.remove(line);
                line.geometry.dispose();
                if (line.material instanceof LineBasicMaterial)
                    line.material.dispose();
            }
        }
    }
    /// Private trackable object helper methods ///
    createTrackableObjectMap(debugTextInfo, trackableObjects) {
        debugTextInfo.modelTrackableObjectMap.clear();
        const activeUIDs = new Set(trackableObjects.map((trackableObject) => trackableObject.uid));
        for (const uid of Array.from(debugTextInfo.labelOffsetMap.keys())) {
            if (!activeUIDs.has(uid))
                debugTextInfo.labelOffsetMap.delete(uid);
        }
        for (const uid of Array.from(debugTextInfo.labelAnchorMap.keys())) {
            if (!activeUIDs.has(uid))
                debugTextInfo.labelAnchorMap.delete(uid);
        }
        for (const uid of Array.from(debugTextInfo.lineTargetMap.keys())) {
            if (!activeUIDs.has(uid))
                debugTextInfo.lineTargetMap.delete(uid);
        }
        for (const trackableObject of trackableObjects) {
            debugTextInfo.modelTrackableObjectMap.set(trackableObject.uid, trackableObject);
        }
    }
    /// Private text helper methods ///
    createDebugText(trackableObject) {
        var _a, _b, _c, _d;
        const label = new Text();
        label.text = trackableObject.name;
        label.anchorX = 'center';
        label.anchorY = 'middle';
        label.fontSize = (_a = trackableObject.fontSize) !== null && _a !== void 0 ? _a : 0.06;
        label.color = (_b = trackableObject.color) !== null && _b !== void 0 ? _b : "black";
        label.outlineColor = (_c = trackableObject.outlineColor) !== null && _c !== void 0 ? _c : "black";
        label.outlineWidth = (_d = trackableObject.outlineWidth) !== null && _d !== void 0 ? _d : 0;
        label.renderOrder = DebugTextService.LABEL_RENDER_ORDER;
        configurePostprocessingOverlay(label);
        label.depthOffset = -10;
        label.frustumCulled = false;
        label.position.copy(this.computeLabelPosition(trackableObject));
        label.quaternion.copy(trackableObject.orientation.clone()).multiply(new Quaternion(0, 1, 0, 0));
        label.sync(() => {
            this.configureTextMaterial(label);
        });
        return label;
    }
    createDebugLine(trackableObject) {
        var _a;
        const start = this.computeLineAnchorPosition(trackableObject);
        const end = this.computeObjectCenterPosition(trackableObject);
        const lineGeometry = new BufferGeometry().setFromPoints([start, end]);
        const lineMaterial = new LineBasicMaterial({
            color: (_a = trackableObject.color) !== null && _a !== void 0 ? _a : "black",
            transparent: true,
            opacity: 0.7
        });
        const line = new Line(lineGeometry, lineMaterial);
        line.renderOrder = DebugTextService.LINE_RENDER_ORDER;
        configurePostprocessingOverlay(line);
        line.visible = this.isFiniteVector3(start) && this.isFiniteVector3(end);
        return line;
    }
    computeLabelPosition(trackableObject) {
        var _a, _b;
        const scope = this.findDebugTextInfoForUID(trackableObject.uid);
        const position = (_b = (_a = scope === null || scope === void 0 ? void 0 : scope.labelAnchorMap.get(trackableObject.uid)) === null || _a === void 0 ? void 0 : _a.clone()) !== null && _b !== void 0 ? _b : this.computeObjectAnchorPosition(trackableObject);
        const labelOffset = scope === null || scope === void 0 ? void 0 : scope.labelOffsetMap.get(trackableObject.uid);
        if (labelOffset === undefined)
            return position;
        return this.applyScreenSpaceOffset(position, labelOffset.x, labelOffset.y);
    }
    updateTextToLookAtCamera(debugTextInfo) {
        for (const [uid, text] of debugTextInfo.modelTextMap.entries()) {
            const trackableObject = debugTextInfo.modelTrackableObjectMap.get(uid);
            if (trackableObject !== undefined)
                text.position.copy(this.computeLabelPosition(trackableObject));
            const pos = localPlayerContainer
                .transformPointFromPlatformToWorldCoordinates(camera.perspectiveCam.position.clone());
            text.lookAt(pos);
            const line = debugTextInfo.modelLineMap.get(uid);
            if (trackableObject !== undefined && line !== undefined)
                this.updateDebugLine(line, trackableObject, text);
        }
    }
    updateLabelOffsets(debugTextInfo, trackableObjects) {
        const fixedTargets = trackableObjects.filter((trackableObject) => trackableObject.lockLabelPosition === true);
        for (const fixedTarget of fixedTargets) {
            debugTextInfo.labelOffsetMap.set(fixedTarget.uid, { x: 0, y: 0 });
            debugTextInfo.labelAnchorMap.set(fixedTarget.uid, this.computeObjectAnchorPosition(fixedTarget));
            debugTextInfo.lineTargetMap.set(fixedTarget.uid, this.computeObjectCenterPosition(fixedTarget));
        }
        const dynamicTargets = trackableObjects.filter((trackableObject) => trackableObject.lockLabelPosition !== true);
        const overlapGroups = [];
        for (const trackableObject of dynamicTargets) {
            let attachedGroup;
            for (const group of overlapGroups) {
                const overlapsGroup = group.some((groupTarget) => this.doTargetsOverlap(groupTarget, trackableObject));
                if (overlapsGroup) {
                    attachedGroup = group;
                    break;
                }
            }
            if (attachedGroup === undefined) {
                overlapGroups.push([trackableObject]);
            }
            else {
                attachedGroup.push(trackableObject);
            }
        }
        const groups = overlapGroups.flatMap((group) => this.splitGroupByMedianOriginExtent(group));
        for (const group of groups) {
            const sortedGroup = [...group].sort((a, b) => a.uid - b.uid);
            const groupCenter = new Vector3();
            let totalGroupRadius = 0;
            for (const groupedTarget of sortedGroup) {
                const center = this.computeObjectCenterPosition(groupedTarget);
                groupCenter.add(center);
            }
            groupCenter.multiplyScalar(1 / sortedGroup.length);
            for (const groupedTarget of sortedGroup) {
                const targetRadius = this.computeTargetRadius(groupedTarget, groupCenter);
                totalGroupRadius += targetRadius;
            }
            const groupAnchor = groupCenter.clone();
            if (sortedGroup.length === 1) {
                debugTextInfo.labelOffsetMap.set(sortedGroup[0].uid, { x: 0, y: 0 });
                debugTextInfo.labelAnchorMap.set(sortedGroup[0].uid, groupAnchor.clone());
                debugTextInfo.lineTargetMap.set(sortedGroup[0].uid, groupCenter.clone());
                continue;
            }
            const averageGroupRadius = totalGroupRadius / sortedGroup.length;
            const worldOffset = Math.max(averageGroupRadius * DebugTextService.LABEL_WORLD_OFFSET_FACTOR, DebugTextService.LABEL_MIN_WORLD_OFFSET);
            const worldRadius = Math.max(averageGroupRadius + worldOffset, 0.18);
            const projectedRadius = this.computeProjectedRadius(groupAnchor, worldRadius);
            const radius = Math.max(projectedRadius, 0.06);
            const startAngle = -Math.PI / 2;
            const angleStep = (2 * Math.PI) / sortedGroup.length;
            for (let i = 0; i < sortedGroup.length; i++) {
                const angle = startAngle + angleStep * i;
                const targetUID = sortedGroup[i].uid;
                debugTextInfo.labelOffsetMap.set(targetUID, {
                    x: Math.cos(angle) * radius,
                    y: Math.sin(angle) * radius
                });
                debugTextInfo.labelAnchorMap.set(targetUID, groupAnchor.clone());
                debugTextInfo.lineTargetMap.set(targetUID, groupCenter.clone());
            }
        }
    }
    splitGroupByMedianOriginExtent(group) {
        if (group.length <= 1)
            return [group];
        return this.partitionGroupByMedianChecks([...group].sort((a, b) => a.uid - b.uid));
    }
    partitionGroupByMedianChecks(group) {
        if (group.length <= 1)
            return [group];
        const medianBounds = this.computeMedianBounds(group);
        const keptTargets = group.filter((target) => medianBounds.containsPoint(target.position));
        const removedTargets = group.filter((target) => !keptTargets.some((keptTarget) => keptTarget.uid === target.uid));
        if (removedTargets.length === 0 || keptTargets.length === 0) {
            return [group];
        }
        return [
            ...this.partitionGroupByMedianChecks(keptTargets),
            ...this.partitionGroupByMedianChecks(removedTargets)
        ];
    }
    applyScreenSpaceOffset(worldPosition, offsetX, offsetY) {
        const cameraWorldPosition = localPlayerContainer
            .transformPointFromPlatformToWorldCoordinates(camera.perspectiveCam.position.clone());
        const distance = Math.max(cameraWorldPosition.distanceTo(worldPosition), 0.001);
        const verticalSpan = 2 * distance * Math.tan((camera.perspectiveCam.fov * Math.PI) / 360);
        const horizontalSpan = verticalSpan * camera.perspectiveCam.aspect;
        const cameraDirection = new Vector3();
        camera.perspectiveCam.getWorldDirection(cameraDirection);
        const cameraWorldQuaternion = new Quaternion();
        camera.perspectiveCam.getWorldQuaternion(cameraWorldQuaternion);
        const cameraUp = new Vector3();
        cameraUp.copy(camera.perspectiveCam.up).applyQuaternion(cameraWorldQuaternion).normalize();
        const cameraRight = new Vector3().crossVectors(cameraDirection, cameraUp).normalize();
        return worldPosition.clone()
            .add(cameraRight.multiplyScalar((horizontalSpan * offsetX) / 2))
            .add(cameraUp.multiplyScalar((verticalSpan * offsetY) / 2));
    }
    findDebugTextInfoForUID(uid) {
        for (const debugTextInfo of this.textScopes.values()) {
            if (debugTextInfo.modelTrackableObjectMap.has(uid))
                return debugTextInfo;
        }
        return undefined;
    }
    computeObjectAnchorPosition(trackableObject) {
        return this.isFiniteVector3(trackableObject.anchorPosition)
            ? trackableObject.anchorPosition.clone()
            : trackableObject.position.clone();
    }
    computeObjectCenterPosition(trackableObject) {
        return this.isFiniteVector3(trackableObject.centerPosition)
            ? trackableObject.centerPosition.clone()
            : trackableObject.position.clone();
    }
    computeLineAnchorPosition(trackableObject) {
        const labelPosition = this.computeLabelPosition(trackableObject);
        return this.isFiniteVector3(labelPosition) ? labelPosition : this.computeObjectCenterPosition(trackableObject);
    }
    updateDebugLine(line, trackableObject, text) {
        var _a, _b;
        const scope = this.findDebugTextInfoForUID(trackableObject.uid);
        const start = this.computeRenderedTextCenter(text);
        const end = (_b = (_a = scope === null || scope === void 0 ? void 0 : scope.lineTargetMap.get(trackableObject.uid)) === null || _a === void 0 ? void 0 : _a.clone()) !== null && _b !== void 0 ? _b : this.computeObjectCenterPosition(trackableObject);
        const points = [start, end];
        line.geometry.setFromPoints(points);
        line.visible = this.isFiniteVector3(start) && this.isFiniteVector3(end);
    }
    isFiniteVector3(vector) {
        return Number.isFinite(vector.x) && Number.isFinite(vector.y) && Number.isFinite(vector.z);
    }
    computeProjectedRadius(worldPosition, worldRadius) {
        const cameraWorldPosition = localPlayerContainer
            .transformPointFromPlatformToWorldCoordinates(camera.perspectiveCam.position.clone());
        const distance = Math.max(cameraWorldPosition.distanceTo(worldPosition), 0.001);
        const verticalSpan = 2 * distance * Math.tan((camera.perspectiveCam.fov * Math.PI) / 360);
        return worldRadius / Math.max(verticalSpan / 2, 0.001);
    }
    configureTextMaterial(text) {
        configurePostprocessingOverlay(text);
        const material = text.material;
        const materials = Array.isArray(material) ? material : [material];
        for (const textMaterial of materials) {
            textMaterial.transparent = true;
        }
    }
    computeRenderedTextCenter(text) {
        const textRenderInfo = text.textRenderInfo;
        const blockBounds = textRenderInfo === null || textRenderInfo === void 0 ? void 0 : textRenderInfo.blockBounds;
        if (blockBounds === undefined)
            return text.position.clone();
        const [minX, minY, maxX, maxY] = blockBounds;
        const localCenter = new Vector3((minX + maxX) * 0.5, (minY + maxY) * 0.5, 0);
        return text.localToWorld(localCenter);
    }
    doTargetsOverlap(targetA, targetB) {
        const boundsA = this.getTargetBounds(targetA);
        const boundsB = this.getTargetBounds(targetB);
        return boundsA.intersectsBox(boundsB);
    }
    getTargetBounds(trackableObject) {
        const hasFiniteBounds = this.isFiniteVector3(trackableObject.box.min) && this.isFiniteVector3(trackableObject.box.max);
        if (hasFiniteBounds)
            return trackableObject.box.clone();
        const radius = 0.05;
        return new Box3(trackableObject.position.clone().addScalar(-radius), trackableObject.position.clone().addScalar(radius));
    }
    computeMedianBounds(targets) {
        const min = new Vector3(this.computeMedian(targets.map((target) => this.getTargetBounds(target).min.x)), this.computeMedian(targets.map((target) => this.getTargetBounds(target).min.y)), this.computeMedian(targets.map((target) => this.getTargetBounds(target).min.z)));
        const max = new Vector3(this.computeMedian(targets.map((target) => this.getTargetBounds(target).max.x)), this.computeMedian(targets.map((target) => this.getTargetBounds(target).max.y)), this.computeMedian(targets.map((target) => this.getTargetBounds(target).max.z)));
        return new Box3(new Vector3(Math.min(min.x, max.x), Math.min(min.y, max.y), Math.min(min.z, max.z)), new Vector3(Math.max(min.x, max.x), Math.max(min.y, max.y), Math.max(min.z, max.z)));
    }
    computeMedian(values) {
        if (values.length === 0)
            return 0;
        const sortedValues = [...values].sort((a, b) => a - b);
        const middleIndex = Math.floor(sortedValues.length / 2);
        if (sortedValues.length % 2 === 1)
            return sortedValues[middleIndex];
        return (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) * 0.5;
    }
    computeTargetRadius(trackableObject, groupCenter) {
        const bounds = this.getTargetBounds(trackableObject);
        const corners = [
            new Vector3(bounds.min.x, bounds.min.y, bounds.min.z),
            new Vector3(bounds.min.x, bounds.min.y, bounds.max.z),
            new Vector3(bounds.min.x, bounds.max.y, bounds.min.z),
            new Vector3(bounds.min.x, bounds.max.y, bounds.max.z),
            new Vector3(bounds.max.x, bounds.min.y, bounds.min.z),
            new Vector3(bounds.max.x, bounds.min.y, bounds.max.z),
            new Vector3(bounds.max.x, bounds.max.y, bounds.min.z),
            new Vector3(bounds.max.x, bounds.max.y, bounds.max.z)
        ];
        let maxRadius = 0;
        for (const corner of corners) {
            maxRadius = Math.max(maxRadius, corner.distanceTo(groupCenter));
        }
        if (maxRadius > 0)
            return maxRadius;
        return this.computeObjectCenterPosition(trackableObject).distanceTo(groupCenter);
    }
}
DebugTextService.LABEL_WORLD_OFFSET_FACTOR = 0.05;
DebugTextService.LABEL_MIN_WORLD_OFFSET = 0.02;
DebugTextService.LABEL_RENDER_ORDER = 9999;
DebugTextService.LINE_RENDER_ORDER = 9998;
export const debugTextService = new DebugTextService();
